import { QuestionCircleOutlined } from '@ant-design/icons';
import { Form, Input, Switch } from 'antd';
import { isFinite } from 'lodash';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import { REALTIME_POTENTIAL_ATTRIBUTE_KEYS } from 'components/damages/constants';
import { renderColumn } from 'components/data/helpers';
import { Button, Tooltip } from 'components/ui';
import { TemperatureOutlined } from 'horizon/components/Images/ImageViewer/Icons/TemperatureIcon';
import { AtlasGqlDamageSchemaAttributeState } from 'types/atlas-graphql';
import { DAMAGE_EDIT_ATTRIBUTES } from 'utils/access-control/rules';
import { editable } from 'utils/editable';
import { useFeatures } from 'utils/features';
import { useIsInView } from 'utils/hooks';
import { usePermissions } from 'utils/usePermissions';

import DamageEditAsset from './DamageEditAsset';
import DamageEditSchema from './DamageEditSchema';

const { TextArea } = Input;

const FormContainer = styled.div`
  max-height: 100%;
  overflow-y: auto;

  ${({ $isScrolledToTop, theme }) =>
    !$isScrolledToTop &&
    `
    border-top: 1px solid ${theme.borderAccentColor};
    -webkit-box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
    -moz-box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
    box-shadow: inset 0px 5px 5px -3px ${theme.lightShadow};
  `}

  form {
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;

    .form-fields-container {
      height: 100%;
      overflow-y: auto;
    }
  }
`;

const StyledForm = styled(Form)`
  width: 100%;

  .ant-form-item {
    margin-bottom: 1rem;
    .ant-form-item-control {
      text-align: start;
    }
    .ant-form-item-control-input-content {
      display: flex;
      flex-direction: column;
      text-align: start;

      &:has(.ant-switch) {
        display: block;
      }
    }
  }
`;

const HintText = styled.span`
  color: ${({ theme }) => theme.gray};
`;

const FixedButtonContainer = styled.div`
  background-color: ${({ backgroundColor, theme }) =>
    backgroundColor ?? theme.secondaryBackgroundColor};
  border-top: 1px solid ${({ theme }) => theme.lightShadow};
  padding: ${({ $isImageViewer }) => ($isImageViewer ? '12px' : '12px 12px 0 12px')};

  display: flex;
  align-items: flex-start;
  justify-content: flex-end;

  ${({ $isScrolledToBottom, theme }) =>
    !$isScrolledToBottom &&
    `
    -webkit-box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
    -moz-box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
    box-shadow: 0px -5px 5px -3px ${theme.lightShadow};
  `}

  button {
    width: 40%;
  }
`;

const StyledButton = styled(Button)`
  margin-right: 12px;
  display: block;
`;

const LabelActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  padding: 0;
  border: 0;
  background: transparent;
  color: ${({ theme }) => theme.teal};
  cursor: pointer;
  font-size: 12px;

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }
`;

const formItemLayout = {
  horizontal: {
    labelCol: {
      xs: { span: 12 },
    },
    wrapperCol: {
      xs: { span: 12 },
    },
  },
  vertical: {
    wrapperCol: {
      xs: { span: 24 },
    },
  },
};

function DisabledExtra({ name }) {
  const { t } = useTranslation();
  return (
    <Tooltip
      title={t(
        'damage.attributeWillBeRemovedTooltip',
        '"{{name}}" will be removed after saving. Attribute is no longer in the schema',
        { name }
      )}
    >
      <div style={{ color: 'red', fontSize: 10, whiteSpace: 'normal' }}>
        {t('damage.attributeWillBeRemoved', '* "{{name}}" will be removed', { name })}
      </div>
    </Tooltip>
  );
}

function InvalidExtra() {
  const { t } = useTranslation();
  return (
    <div style={{ color: 'red', fontSize: 10, whiteSpace: 'normal' }}>
      {t('damage.invalidSelection', 'Invalid Selection')}
    </div>
  );
}

const HelpTooltip = styled(Tooltip)`
  margin-right: 0.2rem;
`;

function stopPropagation(e) {
  e.stopPropagation();
}

export function AttributesForm({
  selections,
  saveButtonText,
  showComment = true,
  columns,
  onChange,
  loading,
  saving,
  saveDisabled,
  onSave,
  onCancel,
  formLayout,
  canChangeAsset = false,
  assetId,
  setAssetId,
  canChangeSchema = false,
  schemaId,
  setSchemaId,
  canChangeCritical = false,
  critical,
  setCritical,
  includesRealtimePotentialAttrs,
  potentialAttrs,
  potentialAssets,
  backgroundColor,
  isImageViewer = false,
  onRequestDeltaTMeasurement,
  deltaTMeasurementEnabled = false,
}) {
  const { t } = useTranslation();
  const submitLock = useRef(false);
  const topRef = useRef(null);
  const bottomRef = useRef(null);
  const isScrolledToTop = useIsInView(topRef);
  const isScrolledToBottom = useIsInView(bottomRef);

  const [form] = Form.useForm();

  const { REQUIRE_VALID_DAMAGE } = useFeatures().features;
  const { canAccess } = usePermissions();
  const isEditable = canAccess([DAMAGE_EDIT_ATTRIBUTES]);

  return (
    <FormContainer $isScrolledToTop={isScrolledToTop}>
      <StyledForm
        form={form}
        id="create-new-damage-attributes-form"
        layout={formLayout}
        onFinish={e => {
          if (!submitLock.current) {
            onSave(e);
          }
        }}
      >
        <div className="form-fields-container">
          <div ref={topRef} />
          {canChangeAsset && (
            <DamageEditAsset
              form={form}
              assetId={assetId}
              onChange={setAssetId}
              potentialAssets={potentialAssets}
              externalLoading={loading}
            />
          )}
          {canChangeSchema && (
            <DamageEditSchema
              form={form}
              schemaId={schemaId}
              onChange={setSchemaId}
              externalLoading={loading}
            />
          )}
          {canChangeCritical && (
            <Form.Item
              {...formItemLayout[formLayout]}
              label={t('table.critical', 'Critical')}
              name="critical"
              initialValue={critical}
            >
              <Switch
                data-testid="critical-input"
                checked={critical}
                onChange={setCritical}
                disabled={!isEditable}
              />
            </Form.Item>
          )}
          {columns.map(col => {
            const { name, valid, state, disabled, description, unit, filters, type } = col;

            const isRealtimeAttribute =
              includesRealtimePotentialAttrs &&
              REALTIME_POTENTIAL_ATTRIBUTE_KEYS.includes(name) &&
              isFinite(potentialAttrs[name]);

            const [min, max] = filters;
            const tooltip = description && (
              <HelpTooltip title={description}>
                <QuestionCircleOutlined />
              </HelpTooltip>
            );
            return (
              <Form.Item
                {...formItemLayout[formLayout]}
                key={name}
                label={
                  <span>
                    {tooltip}
                    {name}
                    {unit && <span style={{ paddingLeft: '0.2rem' }}>({unit})</span>}
                    {['Delta T', 'Delta T (ΔT)'].includes(name) && onRequestDeltaTMeasurement && (
                      <Tooltip
                        title={t(
                          'damage.measureDeltaTTooltip',
                          'Measure Delta T from a reference temperature box'
                        )}
                      >
                        <LabelActionButton
                          type="button"
                          onClick={event => {
                            event.preventDefault();
                            event.stopPropagation();
                            onRequestDeltaTMeasurement();
                          }}
                          disabled={!deltaTMeasurementEnabled}
                        >
                          <TemperatureOutlined />
                        </LabelActionButton>
                      </Tooltip>
                    )}
                  </span>
                }
                extra={
                  !valid && !loading ? (
                    <InvalidExtra />
                  ) : state === AtlasGqlDamageSchemaAttributeState.Disabled && !loading ? (
                    <DisabledExtra name={name} />
                  ) : null
                }
              >
                {renderColumn(
                  editable(col, {
                    onChange,
                    setSubmitLock:
                      type === 'number'
                        ? lock => {
                            submitLock.current = lock;
                          }
                        : undefined,
                  }),
                  {
                    attrs: selections,
                    disabled,
                    editable: isEditable && !isRealtimeAttribute,
                    REQUIRE_VALID_DAMAGE,
                    allowClear: true,
                    ...(col.unit ? { suffix: col.unit } : {}),
                    loading: loading && !Object.keys(selections).includes(name),
                  }
                )}
                <HintText>
                  {isRealtimeAttribute &&
                    t(
                      'damage.valueCalculatedByBoundingBox',
                      'This value is calculated by the bounding box'
                    )}
                  {!isRealtimeAttribute &&
                    type === 'number' &&
                    `${t('damage.range', 'Range:')} ${!!Number(min?.value) ? min.value : 0} ${
                      !!Number(max?.value)
                        ? t('damage.rangeTo', 'to {{max}}', { max: max.value })
                        : t('damage.andUp', 'and up')
                    }`}
                </HintText>
              </Form.Item>
            );
          })}
          {showComment && (
            <Form.Item
              {...formItemLayout[formLayout]}
              label={t('comments.comment', 'Comment')}
              name="comment"
            >
              <TextArea
                id="comment-text-input"
                data-testid="comment-text-input"
                disabled={!isEditable}
                rows={2}
                onKeyDown={stopPropagation}
                onKeyUp={stopPropagation}
              />
            </Form.Item>
          )}
          <div ref={bottomRef} />
        </div>
        {onSave && (
          <FixedButtonContainer
            $isScrolledToBottom={isScrolledToBottom}
            backgroundColor={backgroundColor}
            $isImageViewer={isImageViewer}
          >
            <StyledButton
              data-testid="damage-saveBtn"
              disabled={saveDisabled}
              htmlType="submit"
              loading={saving}
              role="saveButton"
              type="primary"
            >
              {saveButtonText}
            </StyledButton>
            <Button data-testid="damage-cancelBtn" onClick={onCancel}>
              {t('common.cancel', 'Cancel')}
            </Button>
          </FixedButtonContainer>
        )}
      </StyledForm>
    </FormContainer>
  );
}
